import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SuperHead } from "@/pages/super/SuperLayout";
import { useAuth } from "@/context/AuthContext";
import { api, gbp, formatApiError } from "@/lib/api";

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-card border border-border p-5" data-testid={testid}>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="font-display text-2xl font-extrabold mt-1">{value}</p>
  </div>
);

const PAID = ["active", "trialing", "past_due"];

const PlanCell = ({ c, plans, onDone }) => {
  if (c.stripe_subscription_id && PAID.includes(c.subscription_status)) {
    const p = plans.find((x) => x.id === c.plan_id);
    return <span className="text-sm" data-testid={`super-clinic-plan-${c.id}`}>{p?.name || "Plan"} · paid ({c.subscription_status.replace("_", " ")})</span>;
  }
  const value = c.subscription_status === "comp" && c.plan_id ? c.plan_id : "none";
  return (
    <Select value={value} onValueChange={async (v) => {
      try { await api.patch(`/super/clinics/${c.id}/plan`, { plan_id: v === "none" ? null : v }); toast.success("Plan updated"); onDone(); } catch (e) { toast.error(formatApiError(e)); }
    }}>
      <SelectTrigger className="w-44 h-9 text-xs" data-testid={`super-clinic-plan-select-${c.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none">No plan</SelectItem>
        {plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} (free, assigned)</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export default function SuperClinics() {
  const { loginAs } = useAuth();
  const [stats, setStats] = useState(null);
  const [money, setMoney] = useState(null);
  const [clinics, setClinics] = useState([]);
  const [plans, setPlans] = useState([]);
  const load = () => {
    api.get("/super/stats").then((r) => setStats(r.data));
    api.get("/super/payouts").then((r) => setMoney(r.data));
    api.get("/super/clinics").then((r) => setClinics(r.data));
    api.get("/super/plans").then((r) => setPlans(r.data));
  };
  useEffect(load, []);
  const toggle = async (c, approved) => {
    try { await api.patch(`/super/clinics/${c.id}`, { approved }); toast.success(approved ? "Clinic approved" : "Clinic hidden"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const impersonate = async (c) => {
    try { await loginAs(c.id); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <SuperHead eyebrow="Platform admin" title="Marketplace overview" />
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Kpi label="Clinics" value={stats.clinics} testid="super-stat-clinics" />
          <Kpi label="Listed" value={stats.listed_clinics} testid="super-stat-listed" />
          <Kpi label="Awaiting approval" value={stats.pending_clinics} testid="super-stat-pending" />
          <Kpi label="Patients" value={stats.patients} testid="super-stat-patients" />
          <Kpi label="Booking value" value={gbp(stats.revenue)} testid="super-stat-revenue" />
        </div>
      )}
      {money && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
          <Kpi label="Clinics with Stripe connected" value={money.connected_clinics} testid="super-stat-connected" />
          <Kpi label="Platform fees (total)" value={gbp(money.fees)} testid="super-stat-fees-total" />
          <Kpi label="Fees invoiced to clinics" value={gbp(money.fees_billed)} testid="super-stat-fees-billed" />
          <Kpi label="Fees not yet invoiced" value={gbp(money.fees_unbilled)} testid="super-stat-fees-unbilled" />
        </div>
      )}
      <div className="rounded-2xl bg-card border border-border overflow-x-auto mt-8">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Clinic</TableHead><TableHead>Owner</TableHead><TableHead>Plan</TableHead><TableHead>Bookings</TableHead><TableHead>Revenue</TableHead><TableHead>Approved</TableHead><TableHead /></TableRow>
          </TableHeader>
          <TableBody>
            {clinics.map((c) => (
              <TableRow key={c.id} data-testid={`super-clinic-row-${c.id}`}>
                <TableCell>
                  {c.listed ? <Link to={`/clinic/${c.slug}`} className="font-medium hover:text-primary">{c.name}</Link> : <span className="font-medium">{c.name}</span>}
                  <p className="text-xs text-muted-foreground">{c.city} · {c.listed ? "Listed" : "Hidden"}{c.sample ? " · sample" : ""} · Stripe {c.connect_status === "active" ? "connected" : c.connect_status ? "in setup" : "not connected"}</p>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{c.owner?.email || "—"}</TableCell>
                <TableCell><PlanCell c={c} plans={plans} onDone={load} /></TableCell>
                <TableCell>{c.bookings}</TableCell>
                <TableCell>{gbp(c.revenue)}</TableCell>
                <TableCell><Switch checked={c.approved} onCheckedChange={(v) => toggle(c, v)} data-testid={`super-approve-switch-${c.id}`} /></TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" className="rounded-full" disabled={!c.owner} onClick={() => impersonate(c)} data-testid={`super-login-as-button-${c.id}`}>
                    <LogIn className="h-3.5 w-3.5 mr-1.5" />Log in as
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
